import { useState } from "react";
import classNames from "classnames";
import {
  ToggleGroup,
  ToggleGroupItem,
} from "components/DesignSystem/ToggleGroup/ToggleGroup";
import { Button } from "components/DesignSystem/Button/Button";
import Modal from "components/DesignSystem/Modal/Modal";
import { Accordion } from "components/DesignSystem/Accordion/Accordion";
import { Search } from "components/DesignSystem/Search/Search";
import { NumericInput } from "components/NumericInput/NumericInput";
import ToolTip from "components/design/toolTip";
import { Info } from "components/icons/Info";
import {
  Combobox,
  OptionData,
} from "components/DesignSystem/Combobox/Combobox";
import { FormikForm } from "components/FormikForm/FormikForm";
import { PriceInput } from "components/PriceInput/PriceInput";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useToast } from "hooks/useToast";
import { BackendError } from "types/utils/error";
import { DeleteIcon } from "components/icons/delete";
import Async from "components/DesignSystem/AsyncComponents/Async";
import { useCurrentEntityStates } from "components/Entity/EntityFormEngine/AddJurisdiction";
import Loader from "components/DesignSystem/Loader/loader";
import {
  SITApportionmentFactor,
  SITNexusThreshold,
  SITNexusThresholdPayload,
  SITSaleType,
  SITSettingsResponse,
} from "types/Models/stateIncomeTax";
import {
  useGetSITSettingsQuery,
  useSaveSITSettingsMutation,
} from "store/apis/stateIncomeTax";
import { SITSettingsSchema } from "formValidations/SITSettingsSchema";

type TaxBracketRow = {
  from_amount: string;
  to_amount: string;
  tax_rate: string;
  flat_amount: string;
};

type NexusThresholdForm = {
  sales_amount_threshold: string;
  sales_percent_threshold: string;
  payroll_amount_threshold: string;
  payroll_percent_threshold: string;
  property_amount_threshold: string;
  property_percent_threshold: string;
};

type SettingsFormValues = {
  apportionment_factor: SITApportionmentFactor;
  brackets: TaxBracketRow[];
  nexus_product: NexusThresholdForm;
  nexus_service: NexusThresholdForm;
};

const APPORTIONMENT_OPTIONS: {
  value: SITApportionmentFactor;
  label: string;
}[] = [
  { value: "SINGLE_FACTOR", label: "Single Factor" },
  { value: "EQUALLY_WEIGHTED", label: "Equally Weighted" },
  { value: "DOUBLE_WEIGHTED", label: "Double Weighted" },
  { value: "ELEVEN_WEIGHTED", label: "Eleven Weighted" },
];

const DEFAULT_BRACKET: TaxBracketRow = {
  from_amount: "0",
  to_amount: "",
  tax_rate: "",
  flat_amount: "",
};

type Props = {
  isOpen: boolean;
  close: () => void;
  season: number;
};

const toNexusForm = (
  threshold: SITNexusThreshold | undefined
): NexusThresholdForm => ({
  sales_amount_threshold: threshold?.sales_amount_threshold ?? "",
  sales_percent_threshold: threshold?.sales_percent_threshold ?? "",
  payroll_amount_threshold: threshold?.payroll_amount_threshold ?? "",
  payroll_percent_threshold: threshold?.payroll_percent_threshold ?? "",
  property_amount_threshold: threshold?.property_amount_threshold ?? "",
  property_percent_threshold: threshold?.property_percent_threshold ?? "",
});

const toNexusPayload = (
  form: NexusThresholdForm,
  sale_type: SITSaleType
): SITNexusThresholdPayload => {
  const toNum = (v: string) => (v === "" ? null : Number(v));
  return {
    sale_type,
    sales_amount_threshold: toNum(form.sales_amount_threshold),
    sales_percent_threshold: toNum(form.sales_percent_threshold),
    payroll_amount_threshold: toNum(form.payroll_amount_threshold),
    payroll_percent_threshold: toNum(form.payroll_percent_threshold),
    property_amount_threshold: toNum(form.property_amount_threshold),
    property_percent_threshold: toNum(form.property_percent_threshold),
  };
};

const getInitialValues = (
  settingsData: SITSettingsResponse | undefined,
  stateUuid: string
): SettingsFormValues => {
  const stateSettings = settingsData?.data.find(
    (s) => s.state_uuid === stateUuid
  );

  const productThreshold = stateSettings?.nexus_thresholds?.find(
    (t) => t.sale_type === "PRODUCT"
  );
  const serviceThreshold = stateSettings?.nexus_thresholds?.find(
    (t) => t.sale_type === "SERVICE"
  );

  return {
    apportionment_factor:
      stateSettings?.apportionment_factor ?? "SINGLE_FACTOR",
    brackets: stateSettings
      ? [...stateSettings.tax_rate_conditions]
          .sort((a, b) => a.order - b.order)
          .map((c) => ({
            from_amount: c.from_amount ?? "",
            to_amount: c.to_amount ?? "",
            tax_rate: c.tax_rate ?? "",
            flat_amount: c.flat_amount ?? "",
          }))
      : [],
    nexus_product: toNexusForm(productThreshold),
    nexus_service: toNexusForm(serviceThreshold),
  };
};

type NexusThresholdSectionProps = {
  label: string;
  amountFieldName: string;
  percentFieldName: string;
};

const NexusThresholdSection = ({
  label,
  amountFieldName,
  percentFieldName,
}: NexusThresholdSectionProps) => (
  <div className="t-flex t-flex-col t-gap-2">
    <span className="t-text-subtitle-sm ">{label}</span>
    <div className="t-grid t-grid-cols-2 t-gap-3">
      <div className="t-flex t-flex-col t-gap-1">
        <PriceInput
          name={amountFieldName}
          customSize="small"
          placeholder="0.00"
          hideError
          label="Amount"
        />
      </div>
      <div className="t-flex t-flex-col t-gap-1">
        <NumericInput
          fieldProps={{ name: percentFieldName }}
          numericProps={{
            placeholder: "0.00",
            suffix: "%",
            decimalScale: 4,
            customSize: "small",
            label: "Percent",
          }}
        />
      </div>
    </div>
  </div>
);

type StateSettingsFormProps = {
  selectedState: {
    name: string;
    country: string;
    state_id: string;
  };
  season: number;
  onSaveSuccess: () => void;
};

const StateSettingsForm = ({
  season,
  selectedState,
  onSaveSuccess,
}: StateSettingsFormProps) => {
  const { uuid: groupId } = useCurrentGroupContext();
  const entityId = useCurrentEntityId();
  const stateUuid = selectedState.state_id;
  const { alertToast } = useToast();
  const [nexusSaleType, setNexusSaleType] = useState<SITSaleType>("PRODUCT");

  const { data: settingsData, isLoading } = useGetSITSettingsQuery(
    { groupId, entityId, season },
    { skip: !groupId || !entityId }
  );

  const [saveSettings] = useSaveSITSettingsMutation();

  const handleSubmit = async (values: SettingsFormValues) => {
    try {
      await saveSettings({
        groupId,
        entityId,
        season,
        state_uuid: stateUuid,
        apportionment_factor: values.apportionment_factor,
        tax_rate_conditions: values.brackets.map((b, index) => ({
          from_amount: b.from_amount === "" ? null : b.from_amount,
          to_amount: b.to_amount === "" ? null : b.to_amount,
          tax_rate: b.tax_rate !== "" ? b.tax_rate : null,
          flat_amount: b.flat_amount !== "" ? b.flat_amount : null,
          order: index,
        })),
        nexus_thresholds: [
          toNexusPayload(values.nexus_product, "PRODUCT"),
          toNexusPayload(values.nexus_service, "SERVICE"),
        ],
      }).unwrap();
      onSaveSuccess();
    } catch (error) {
      alertToast({ message: (error as BackendError).data?.error?.message });
    }
  };

  if (isLoading) return <Loader />;

  const initialValues = getInitialValues(settingsData, stateUuid);
  const nexusPrefix =
    nexusSaleType === "PRODUCT" ? "nexus_product" : "nexus_service";

  return (
    <FormikForm
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={SITSettingsSchema}
    >
      {({ values, setFieldValue, isSubmitting, dirty, resetForm }) => (
        <div className="t-absolute t-inset-0 t-flex t-flex-col">
          <div className="t-flex-shrink-0 t-border-0 t-border-b t-border-solid t-border-neutral-10 t-px-6 t-py-4">
            <h3 className="t-m-0 t-text-subtitle t-font-semibold t-text-text-100">
              {selectedState.name}
            </h3>
            <span className="t-text-body-sm t-text-text-30">
              Configure tax settings for this state
            </span>
          </div>
          <div className="t-overflow-y-auto t-p-6 t-pb-24">
            <Accordion.Root
              type="multiple"
              defaultValue={["tax_rate", "apportionment", "nexus_thresholds"]}
            >
              <div className="t-flex t-flex-col t-gap-3">
                <Accordion.Item value="nexus_thresholds">
                  <Accordion.Trigger>Nexus Thresholds</Accordion.Trigger>
                  <Accordion.Content>
                    <div className="t-flex t-flex-col t-gap-4">
                      <ToggleGroup
                        value={nexusSaleType}
                        onValueChange={(v) =>
                          v && setNexusSaleType(v as SITSaleType)
                        }
                      >
                        <ToggleGroupItem value="PRODUCT">
                          Product
                        </ToggleGroupItem>
                        <ToggleGroupItem value="SERVICE">
                          Service
                        </ToggleGroupItem>
                      </ToggleGroup>

                      <div className="t-flex t-flex-col t-gap-4">
                        <NexusThresholdSection
                          label="Sales"
                          amountFieldName={`${nexusPrefix}.sales_amount_threshold`}
                          percentFieldName={`${nexusPrefix}.sales_percent_threshold`}
                        />
                        <NexusThresholdSection
                          label="Payroll"
                          amountFieldName={`${nexusPrefix}.payroll_amount_threshold`}
                          percentFieldName={`${nexusPrefix}.payroll_percent_threshold`}
                        />
                        <NexusThresholdSection
                          label="Property"
                          amountFieldName={`${nexusPrefix}.property_amount_threshold`}
                          percentFieldName={`${nexusPrefix}.property_percent_threshold`}
                        />
                      </div>
                    </div>
                  </Accordion.Content>
                </Accordion.Item>

                <Accordion.Item value="tax_rate">
                  <Accordion.Trigger>
                    <span className="t-flex t-items-center t-gap-1.5">
                      Tax Rate
                      <ToolTip
                        text={
                          <div className="t-flex t-flex-col t-gap-2 t-text-left">
                            <p className="t-m-0 t-font-semibold">
                              How tax brackets work
                            </p>
                            <ul className="t-m-0 t-flex t-flex-col t-gap-1 t-pl-4">
                              <li>
                                <span className="t-font-medium">
                                  Range + Rate + Fixed Charge :
                                </span>
                                <br />
                                (Income × Apportionment% × Rate%) + Fixed Charge
                              </li>
                              <li>
                                <span className="t-font-medium">
                                  Range + Rate only :
                                </span>
                                <br />
                                Income × Apportionment% × Rate%
                              </li>
                              <li>
                                <span className="t-font-medium">
                                  Range + Fixed Charge only :
                                </span>
                                <br />
                                Fixed charge applied within the range
                              </li>
                              <li>
                                <span className="t-font-medium">
                                  No range + Rate only :
                                </span>
                                <br />
                                Rate% applied to all income
                              </li>
                              <li>
                                <span className="t-font-medium">
                                  No range + Fixed Charge only :
                                </span>
                                <br />
                                Fixed charge regardless of income
                              </li>
                            </ul>
                          </div>
                        }
                        propClass="t-max-w-[320px]"
                        side="right"
                      >
                        <span className="t-text-text-30 t-flex">
                          <Info size="14" color="currentColor" />
                        </span>
                      </ToolTip>
                    </span>
                  </Accordion.Trigger>
                  <Accordion.Content>
                    <div className="t-flex t-flex-col t-gap-3">
                      {values.brackets.length > 0 && (
                        <div className="t-grid t-grid-cols-[180px_180px_125px_105px_32px] t-gap-2">
                          <span className="t-text-body-sm t-text-text-30">
                            From
                          </span>
                          <span className="t-text-body-sm t-text-text-30">
                            To
                          </span>
                          <span className="t-text-body-sm t-text-text-30">
                            Fixed Charge
                          </span>
                          <span className="t-text-body-sm t-text-text-30">
                            Tax Rate
                          </span>
                          <span />
                        </div>
                      )}

                      {values.brackets.map((bracket, index) => (
                        <div key={index} className="t-flex t-flex-col t-gap-1">
                          <div className="t-grid t-grid-cols-[180px_180px_125px_105px_32px] t-gap-2 t-items-center">
                            <PriceInput
                              name={`brackets[${index}].from_amount`}
                              customSize="small"
                              placeholder="0.00"
                              hideError
                            />
                            <PriceInput
                              name={`brackets[${index}].to_amount`}
                              customSize="small"
                              placeholder="No limit"
                              hideError
                            />
                            <PriceInput
                              name={`brackets[${index}].flat_amount`}
                              customSize="small"
                              placeholder="0.00"
                              hideError
                            />
                            <NumericInput
                              fieldProps={{
                                name: `brackets[${index}].tax_rate`,
                              }}
                              numericProps={{
                                placeholder: "0.00",
                                suffix: "%",
                                decimalScale: 2,
                                customSize: "small",
                              }}
                            />
                            <Button
                              customType="ghost_icon"
                              size="small"
                              type="button"
                              disabled={values.brackets.length === 1}
                              onClick={() => {
                                const updated = values.brackets.filter(
                                  (_, i) => i !== index
                                );
                                setFieldValue("brackets", updated);
                              }}
                            >
                              <span className="t-text-text-30">
                                <DeleteIcon color="currentColor" size="16" />
                              </span>
                            </Button>
                          </div>
                        </div>
                      ))}

                      <div>
                        <Button
                          size="small"
                          type="button"
                          onClick={() =>
                            setFieldValue("brackets", [
                              ...values.brackets,
                              { ...DEFAULT_BRACKET },
                            ])
                          }
                        >
                          + Add Bracket
                        </Button>
                      </div>
                    </div>
                  </Accordion.Content>
                </Accordion.Item>

                <Accordion.Item value="apportionment">
                  <Accordion.Trigger>Apportionment Factor</Accordion.Trigger>
                  <Accordion.Content>
                    <Combobox
                      menuPortalTarget={document.body}
                      isSearchable={false}
                      isClearable={false}
                      options={APPORTIONMENT_OPTIONS}
                      value={
                        APPORTIONMENT_OPTIONS.find(
                          (o) => o.value === values.apportionment_factor
                        ) ?? null
                      }
                      onChange={(option) => {
                        const selected = option as OptionData | null;
                        if (selected?.value) {
                          setFieldValue(
                            "apportionment_factor",
                            selected.value as SITApportionmentFactor
                          );
                        }
                      }}
                    />
                  </Accordion.Content>
                </Accordion.Item>
              </div>
            </Accordion.Root>
          </div>

          <div className="t-absolute t-bottom-0 t-left-0 t-right-0 t-flex t-justify-end t-gap-3 t-border-0 t-border-t t-border-solid t-border-neutral-10 t-bg-white t-px-6 t-py-4">
            <Button
              type="button"
              disabled={!dirty || isSubmitting}
              onClick={() => resetForm()}
            >
              Cancel
            </Button>
            <Button
              customType="primary"
              type="submit"
              isLoading={isSubmitting}
              disabled={!dirty || isSubmitting}
            >
              Save
            </Button>
          </div>
        </div>
      )}
    </FormikForm>
  );
};

export const SITSettingsModal = ({ isOpen, close, season }: Props) => {
  const entityId = useCurrentEntityId();
  const { successToast } = useToast();
  const [stateSearch, setStateSearch] = useState("");
  const [selectedStateId, setSelectedStateId] = useState<string | null>(null);

  const { data = [], isLoading, isSuccess } = useCurrentEntityStates(entityId);

  const filteredStates = data.filter((s) =>
    s.name.toLowerCase().includes(stateSearch.toLowerCase())
  );

  const firstStateId = data[0]?.state_id ?? null;
  const resolvedStateId = selectedStateId ?? firstStateId;
  const selectedState = data.find((s) => s.state_id === resolvedStateId);

  return (
    <Modal.Root open={isOpen} onOpenChange={close}>
      <Modal.Content size="xl">
        <Modal.Header>
          <Modal.Title>Tax Settings</Modal.Title>
          <Modal.Close />
        </Modal.Header>
        <Modal.Body className="!t-p-0">
          <Async.Root
            isEmpty={data.length === 0}
            isLoading={isLoading}
            isSuccess={isSuccess}
          >
            <Async.Empty>
              <div className="t-flex t-items-center t-justify-center t-py-16 t-text-body-sm t-text-text-30">
                No states found. Add jurisdictions first.
              </div>
            </Async.Empty>
            <Async.Success>
              <div className="t-flex t-h-[560px]">
                <div className="t-flex t-w-52 t-ml-4 t-pb-4 t-flex-shrink-0 t-flex-col t-border-0 t-border-r t-border-solid t-border-neutral-10">
                  <div className="t-py-3 t-pr-3">
                    <Search
                      placeholder="Search state..."
                      value={stateSearch}
                      onChange={(e) => setStateSearch(e.target.value)}
                      block
                    />
                  </div>
                  <div className="t-flex-1 t-overflow-y-auto">
                    {filteredStates.length === 0 ? (
                      <p className="t-m-0 t-px-4 t-py-3 t-text-body-sm t-text-text-30">
                        No states found.
                      </p>
                    ) : (
                      filteredStates.map((state) => (
                        <button
                          key={state.state_id}
                          className={classNames(
                            "all:unset t-flex t-w-full t-cursor-pointer t-items-center t-px-4 t-py-3 t-text-body-sm t-transition-colors hover:t-bg-surface-lighter-grey",
                            {
                              "t-bg-purple-0 t-font-medium t-text-purple-70":
                                resolvedStateId === state.state_id,
                              "t-text-text-60":
                                resolvedStateId !== state.state_id,
                            }
                          )}
                          onClick={() => setSelectedStateId(state.state_id)}
                        >
                          {state.name}
                        </button>
                      ))
                    )}
                  </div>
                </div>

                <div className="t-relative t-flex-1 t-overflow-hidden">
                  {selectedState && (
                    <StateSettingsForm
                      key={resolvedStateId!}
                      selectedState={selectedState}
                      season={season}
                      onSaveSuccess={() =>
                        successToast({ message: "Settings saved." })
                      }
                    />
                  )}
                </div>
              </div>
            </Async.Success>
          </Async.Root>
        </Modal.Body>
      </Modal.Content>
    </Modal.Root>
  );
};
